<template>
  <section class="py-20" style="background: linear-gradient(180deg, #ffffff 0%, #e6e2fe 60%);">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header Question -->
      <div class="text-center mb-12">
        
        <div class="text-accent-500 uppercase font-semibold text-sm mb-2">Support & Training</div>

        <h2 class="text-4xl md:text-5xl font-headings text-secondary-900 mb-6">
          Flexible and Considered Support 
        </h2>

        <div>No two companies are the same, so we offer flexible options to meet your needs.</div>
      </div>


      <div class="flex flex-col md:flex-row gap-4 mt-18">
        <div class="rounded rounded-tl-4xl bg-gray-100 p-8 md:w-1/3 h-128">
          <div class="mb-6 flex items-center gap-4">
            <Icon :icon="BookOpenIcon" variant="primary" :filled="true" />
            <div>
              <div class="text-2xl font-headings text-primary-500">Entegy Core Support</div>
              <div class="text-sm text-gray-500">Enabling you to manage your event yourself</div>
            </div>
          </div>
          <div class="space-y-3">
            <div class="flex items-start gap-3">
              <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p class="font-semibold text-secondary-900">Self-service activation</p>
                <p class="text-sm text-gray-500">Create your event in seconds, from templates or from scratch
                </p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p class="font-semibold text-secondary-900">Onboarding Sessions</p>
                <p class="text-sm text-gray-500">Get your team up to speed with onboarding sessions</p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p class="font-semibold text-secondary-900">Help Center</p>
                <p class="text-sm text-gray-500">Access to our comprehensive help center with videos, guides and FAQs
                </p>
              </div>
            </div>
            <div class="flex items-start gap-3">
              <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
              <div>
                <p class="font-semibold text-secondary-900">Ticketed Support</p>
                <p class="text-sm text-gray-500">Get quick support from our team of experts</p>
              </div>
            </div>

            <div class="mt-8 text-center  ">
              <TextLink link="https://help.entegy.events" text='View Help Center' />
            </div>
          </div>
        </div>

        <div class="md:w-1/3 h-128">
          <div class="flex flex-col gap-4 h-full">
            <div class="bg-gray-100 h-1/2">
              <img src="../images/photography/entegy04.png" alt="Secure cloud infrastructure"
                class="w-full h-full object-cover" />
            </div>

            <div class="bg-primary-500 p-4 h-1/2">
              <h3 class="text-xl font-headings text-white mb-3">Our Ongoing Commitment to Platform Improvement</h3>
              <p class="text-gray-100">
                Regular updates, features, and continuous refinements. The platform evolves with your feedback and
                industry trends. New features updates are added to the platform and are automatically included for
                current and future events.
              </p>
            </div>
          </div>
        </div>

        <div class="rounded rounded-br-4xl bg-gray-100 md:w-1/3 p-6 h-128 overflow-y-auto">
          <h3 class="text-xl font-headings text-secondary-900 mb-6">Three Ways to Work With Us</h3>
          <div class="space-y-6">
            <div>
              <div class="mb-2 flex items-center gap-4">
                <span class="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-sm font-bold text-white">1</span>
                <div>
                  <div class="font-semibold text-secondary-500">Pay As You Go</div>
                  <p class="text-sm font-medium text-gray-500 mb-1">Simple. Flexible. No commitments.</p>
                </div>
                
              </div>
              
              <p class="text-sm text-gray-600">Run your event, pay for what you need, and move on. Perfect for one-off events, occasional projects, or teams that value maximum flexibility.</p>
            </div>
            <div>
              <div class="mb-2 flex items-center gap-4">
                <span class="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-sm font-bold text-white">2</span>
                <div>
                  <div class="font-semibold text-secondary-500">Subscriptions</div>
                  <p class="text-sm font-medium text-gray-500 mb-1">Better value. Deeper support.</p>
                </div>
              </div>
              <p class="text-sm text-gray-600">If you're running multiple events or planning ahead, subscriptions unlock significant discounts and enhanced support options.</p>
            </div>
            <div>
              <div class="mb-2 flex items-center gap-4">
                <span class="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-500 text-sm font-bold text-white">3</span>
                <div>
                  <div class="font-semibold text-secondary-500">Become a Partner</div>
                  <p class="text-sm font-medium text-gray-500 mb-1">Grow together. Win together.</p>
                </div>
              </div>
              <p class="text-sm text-gray-600">Work with us at a deeper level to deliver exceptional event experiences while building your own recurring revenue stream.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Self-Service Benefits -->
      <div class="grid md:grid-cols-3 gap-6 mb-16 hidden">
        <div class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div
            class="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-secondary-900 mb-3">Intuitive All-in-One Platform</h3>
          <p class="text-secondary-600 leading-relaxed">
            Designed with simplicity in mind. Navigate effortlessly through all features with a clean, logical interface
            that makes sense from day one.
          </p>
        </div>

        <div class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div
            class="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-secondary-900 mb-3">Knowledge Base Loaded with Videos</h3>
          <p class="text-secondary-600 leading-relaxed">
            Comprehensive video tutorials and guides at your fingertips. Learn at your own pace with step-by-step
            walkthroughs for every feature.
          </p>
        </div>

        <div class="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div
            class="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
            <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
            </svg>
          </div>
          <h3 class="text-xl font-bold text-secondary-900 mb-3">Ongoing Commitment to Improvement</h3>
          <p class="text-secondary-600 leading-relaxed">
            Regular updates, new features, and continuous refinements. The platform evolves with your needs and industry
            trends.
          </p>
        </div>
      </div>

      <!-- Reality Check Section -->
      <div
        class=" hidden bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-12 mb-16 border-2 border-orange-200">
        <div class="max-w-4xl mx-auto">
          <div class="flex items-start gap-6 mb-6">
            <div class="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="text-3xl font-bold text-secondary-900 mb-4">But Here's the Reality...</h3>
              <div class="space-y-3 text-lg text-secondary-700 leading-relaxed">
                <p>
                  <span class="font-semibold">Events can be complicated.</span> Like any software, complexity scales
                  with requirements.
                </p>
                <p>
                  <span class="font-semibold">Understanding your data is paramount.</span> The more sophisticated your
                  event, the more important it becomes to have the right guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text-center text-2xl font-semibold text-secondary-900 mt-18">
        Need a bit more than this?        
      </div>
      <div class="text-center text-gray-500 mt-2 mb-18">
        Events and data can get complicated fast...
      </div>

      <!-- Partnership Section -->
      <div class="mb-16">
        <div class="text-center mb-12">
          <h2 class="text-4xl font-headings text-secondary-900 mb-4">
            That's Why We Partner with Experts
          </h2>
          <p class="text-gray-500 max-w-3xl mx-auto">
            We work with a range of fantastic companies around the world who provide professional services tailored to
            your needs
          </p>
        </div>

        <div class="grid md:grid-cols-2 gap-4 mb-12">
          <!-- Professional Services Grid -->
          <div class="bg-white rounded-tl-4xl p-8 shadow-lg border border-primary-500">
            <div class="mb-6 flex items-center gap-4">
              <Icon :icon="BookOpenIcon" variant="primary" :filled="true" />
              <div>
                <div class="text-2xl font-headings text-primary-500">Partner Supported</div>
                <div class="text-sm text-gray-500">A little help along the way along the way, just where you need it
                </div>
              </div>
            </div>
            <div class="space-y-3">
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Additional Training & Education</p>
                  <p class="text-sm text-gray-500">Get your team up to speed with tailored training and support sessions
                  </p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Event Planning & Management</p>
                  <p class="text-sm text-gray-500">Strategic guidance for your event, including content management and
                    stress testing</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Design, Website & Registration Setup</p>
                  <p class="text-sm text-gray-500">Professional design, website setup and registration management to
                    ensure a smooth experience</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-primary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Hardware & Onsite Support</p>
                  <p class="text-sm text-gray-500">Hire kiosks, scanners, and other hardware and the staff to operate
                    them</p>
                </div>
              </div>

              <div class="mt-8 text-center  ">
                <TextLink link="/find-a-partner" text='Find a Partner' />
              </div>
            </div>
          </div>

          <div class="bg-white rounded-br-4xl p-8 shadow-lg border border-secondary-500">
            <div class="mb-6 flex items-center gap-4">
              <Icon :icon="BookOpenIcon" variant="secondary" :filled="true" />
              <div>
                <div class="text-2xl font-headings text-secondary-500">Partner Managed</div>
                <div class="text-sm text-gray-500">Premium support and management so you can focus on your event</div>
              </div>
            </div>
            <div class="space-y-3">
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">End-to-end project delivery</p>
                  <p class="text-sm text-gray-500">From planning to execution, a full-service partner handles everything</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Ongoing Training & Support</p>
                  <p class="text-sm text-gray-500">Continuous support and training to keep your team up to speed</p>
                </div>
              </div>
              <div class="flex items-start gap-3">
                <Checkmark class="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Additional Services for other Partners</p>
                  <p class="text-sm text-gray-500">Manage additianl requirements for your event delivered by other partners, or other services you need for your event</p>
                </div>
              </div>
              <div class="flex items-start gap-3"> 
                <Checkmark class="w-6 h-6 text-secondary-500 flex-shrink-0 mt-1" />
                <div>
                  <p class="font-semibold text-secondary-900">Specialised Account Management</p>
                  <p class="text-sm text-gray-500">Specialised engagement and data management for your organisation, tailored to your specific requirements</p>
                </div>
              </div>
              <div class="mt-8 text-center  ">
                <TextLink link="/contact" text='Contact Us' />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import Icon from '@/components/Buttons/Icon.vue'
import { BookOpenIcon, VideoCameraIcon, SparklesIcon } from '@heroicons/vue/24/outline'
import Checkmark from '@/images/icons/Checkmark.vue'
import TextLink from '@/components/Buttons/TextLink.vue'

</script>

<style scoped>
@keyframes pulse-subtle {

  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }

  50% {
    opacity: 0.9;
    transform: scale(1.02);
  }
}

.animate-pulse-subtle {
  animation: pulse-subtle 3s ease-in-out infinite;
}

.card {
  background-color: #fff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
</style>
